import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useUi } from '../app/context';
import { dismissNotice, type DialogRequest } from '../app/ui';
import { AlertIcon } from './icons';
import { usePresence } from './Popover';

/**
 * Document-modal sheet. Like a macOS sheet it belongs to the editor object,
 * descending from beneath the tabs, rather than taking over the page.
 */
export function Sheet() {
  const dialog = useUi((s) => s.dialog);
  // Keep showing the last request while the sheet animates away.
  const [last, setLast] = useState<DialogRequest | null>(dialog);
  if (dialog && dialog !== last) setLast(dialog);
  const { mounted, closing } = usePresence(!!dialog, 160);
  const ref = useRef<HTMLDivElement>(null);
  const [option, setOption] = useState(false);
  const [prevDialog, setPrevDialog] = useState(dialog);
  if (dialog !== prevDialog) {
    setPrevDialog(dialog);
    setOption(false);
  }

  useEffect(() => {
    if (!dialog) return;
    const chosen = dialog.focusId ? ref.current?.querySelector<HTMLElement>(`[data-action="${dialog.focusId}"]`) : null;
    const primary = chosen ?? ref.current?.querySelector<HTMLElement>('[data-primary]') ?? ref.current?.querySelector<HTMLElement>('button');
    primary?.focus({ preventScroll: true });
  }, [dialog]);

  const shown = dialog ?? last;
  if (!mounted || !shown) return null;

  const keyDown = (e: KeyboardEvent) => {
    if (!dialog) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      dialog.resolve(dialog.cancelId, option);
    } else if (e.key === 'Tab') {
      // Keep focus inside the sheet while it is open.
      const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('button, input') ?? []);
      const i = items.indexOf(document.activeElement as HTMLElement);
      const next = (i + (e.shiftKey ? -1 : 1) + items.length) % items.length;
      e.preventDefault();
      items[next]?.focus();
    }
  };

  return (
    <div className="sheet-layer" data-state={closing ? 'closing' : 'open'} onPointerDown={(e) => e.target === e.currentTarget && dialog?.resolve(dialog.cancelId, option)}>
      <div ref={ref} className="sheet" role="alertdialog" aria-modal="true" aria-labelledby="sheet-title" aria-describedby={shown.message ? 'sheet-message' : undefined} onKeyDown={keyDown}>
        <h2 id="sheet-title" className="sheet-title">
          {shown.title}
        </h2>
        {shown.message && (
          <p id="sheet-message" className="sheet-message">
            {shown.message}
          </p>
        )}
        {shown.details && (
          <ul className="sheet-details">
            {shown.details.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        )}
        {shown.option && (
          <label className="sheet-option">
            <input type="checkbox" checked={option} onChange={(e) => setOption(e.target.checked)} />
            <span>{shown.option.label}</span>
          </label>
        )}
        <div className="sheet-actions">
          {shown.actions.map((a) => (
            <button
              key={a.id}
              type="button"
              className="sheet-btn"
              data-tone={a.tone}
              data-primary={a.tone === 'primary' || undefined}
              data-action={a.id}
              onClick={() => dialog?.resolve(a.id, option)}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** A single quiet line above the pill for outcomes worth stating. */
export function NoticeLine() {
  const notice = useUi((s) => s.notice);
  const [last, setLast] = useState(notice);
  if (notice && notice !== last) setLast(notice);
  const { mounted, closing } = usePresence(!!notice, 180);
  const shown = notice ?? last;
  return (
    <div className="notice-region" role="status" aria-live="polite">
      {mounted && shown && (
        <button type="button" className="notice" data-tone={shown.tone} data-state={closing ? 'closing' : 'open'} key={shown.id} onClick={dismissNotice} aria-label={`${shown.text} Dismiss.`}>
          {shown.tone === 'error' && <AlertIcon size={15} />}
          <span>{shown.text}</span>
        </button>
      )}
    </div>
  );
}
